import { cn } from "@/lib/utils";
import type { InterpretationFacts } from "@/types/interpretation.types";

// Selo de direção e de confiança: SEMPRE cor + dot + rótulo (nunca só cor).
type Tone = { label: string; dot: string; text: string };
const NEUTRAL_TONE: Tone = {
  label: "Estável",
  dot: "bg-muted-foreground",
  text: "text-muted-foreground",
};
const DIRECTION: Record<string, Tone> = {
  IMPROVED: { label: "Melhorou", dot: "bg-emerald-500", text: "text-emerald-700" },
  WORSENED: { label: "Piorou", dot: "bg-destructive", text: "text-destructive" },
  NEUTRAL: NEUTRAL_TONE,
  NO_BASELINE: { label: "Sem base", dot: "bg-muted-foreground", text: "text-muted-foreground" },
};
const INSUFFICIENT_TONE: Tone = {
  label: "Confiança insuficiente",
  dot: "bg-muted-foreground",
  text: "",
};
const CONFIDENCE: Record<string, Tone> = {
  HIGH: { label: "Confiança alta", dot: "bg-emerald-500", text: "" },
  MEDIUM: { label: "Confiança média", dot: "bg-amber-500", text: "" },
  LOW: { label: "Confiança baixa", dot: "bg-orange-500", text: "" },
  INSUFFICIENT: INSUFFICIENT_TONE,
};

function Chip({ dot, label, text }: { dot: string; label: string; text?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-border-subtle px-2 py-0.5 text-[11px] font-medium",
        text ?? "text-text-secondary"
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", dot)} aria-hidden />
      {label}
    </span>
  );
}

export function VerdictSection({
  verdict,
  confidence,
}: {
  verdict: InterpretationFacts["verdict"];
  confidence: InterpretationFacts["confidence"];
}): React.ReactElement {
  const dir = DIRECTION[verdict.direction] ?? NEUTRAL_TONE;
  const conf = CONFIDENCE[confidence.level] ?? INSUFFICIENT_TONE;
  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Chip dot={dir.dot} label={dir.label} text={dir.text} />
        <Chip dot={conf.dot} label={conf.label} />
      </div>
      <p className="text-[15px] font-semibold leading-snug tracking-tight text-text-primary">
        {verdict.headline}
      </p>
      {confidence.causes.length > 0 ? (
        <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">
          {confidence.causes.join(" ")}
        </p>
      ) : null}
    </section>
  );
}
